import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import {
  ReturnLineConditionInput,
  type Order,
  type Return,
} from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useUpdateReturn } from '../hooks/use-return-mutations';
import {
  emptyInspectForm,
  inspectSchema,
  toInspectBody,
  type InspectForm,
} from '../schemas/inspect-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['lines']);
// The backend points at `lines[2].orderLineId` or at `lines` as a whole.
const mapField = (field: string) => (field.startsWith('lines') ? 'lines' : field);

export interface InspectDialogProps {
  storeId: string;
  ret: Return;
  order: Order | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Records the condition of every returned line and marks the return inspected in one PATCH. */
export function InspectDialog({ storeId, ret, order, open, onOpenChange }: InspectDialogProps) {
  const { t } = useTranslation(['returns', 'common']);
  const message = useErrorMessage();
  const update = useUpdateReturn(storeId, 'returns:toasts.inspected');
  const titles = new Map(order?.lines.map((line) => [line.id, line.title]) ?? []);
  const form = useForm<InspectForm>({
    resolver: useI18nResolver(inspectSchema),
    defaultValues: emptyInspectForm(ret),
  });
  const linesError =
    form.formState.errors.lines?.message ?? form.formState.errors.lines?.root?.message;

  const close = () => {
    onOpenChange(false);
    update.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      await update.mutateAsync({ id: ret.id, body: toInspectBody(values) });
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('returns:inspect.title', { rma: ret.rmaNumber })}</DialogTitle>
          <DialogDescription>{t('returns:inspect.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            {linesError ? (
              <p className="text-destructive text-sm" role="alert">
                {linesError}
              </p>
            ) : null}
            {ret.lines.map((item, index) => {
              const title = titles.get(item.orderLineId) ?? t('returns:lines.unknown');
              return (
                <FormField
                  key={item.orderLineId}
                  control={form.control}
                  name={`lines.${index}.condition`}
                  render={({ field, fieldState }) => (
                    <FormItem className="flex flex-row items-start justify-between gap-3 rounded-md border p-3">
                      <div className="min-w-0 space-y-0.5">
                        <FormLabel className="font-medium">{title}</FormLabel>
                        <p className="text-muted-foreground text-xs">
                          {t('returns:inspect.units', { count: item.quantity })}
                        </p>
                        <FormMessage />
                      </div>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger
                            className="w-[150px]"
                            aria-label={t('returns:inspect.conditionFor', { title })}
                            aria-invalid={fieldState.invalid}
                          >
                            <SelectValue placeholder={t('returns:inspect.choose')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.values(ReturnLineConditionInput).map((condition) => (
                            <SelectItem key={condition} value={condition}>
                              {t(`returns:condition.${condition}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              );
            })}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? t('returns:inspect.submitting') : t('returns:inspect.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
